import React, { useState } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  Clock, MapPin, Users, Video, Scale, Bell
} from 'lucide-react';

const events = [
  { id: 1, title: 'WP/1234/2024 Hearing', time: '10:30 AM', type: 'hearing', location: 'Delhi High Court', color: 'blue' },
  { id: 2, title: 'Client Meeting - Sharma', time: '2:00 PM', type: 'meeting', location: 'Office', color: 'green' },
  { id: 3, title: 'Document Filing Deadline', time: '5:00 PM', type: 'deadline', location: '-', color: 'red' },
  { id: 4, title: 'Team Review Call', time: '6:00 PM', type: 'call', location: 'Video', color: 'purple' },
];

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const CalendarPage = () => {
  const { isDark } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  return (
    <MainLayout title="Calendar" subtitle="Manage your schedule">
      <div className="grid grid-cols-3 gap-6">
        {/* Calendar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="col-span-2 glass-card p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <motion.button
                onClick={prevMonth}
                className="p-2 rounded-lg hover:bg-gray-500/10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeft className="w-5 h-5" />
              </motion.button>
              <h2 className="text-xl font-bold">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <motion.button
                onClick={nextMonth}
                className="p-2 rounded-lg hover:bg-gray-500/10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRight className="w-5 h-5" />
              </motion.button>
            </div>
            <motion.button
              className="flex items-center gap-2 px-4 py-2 rounded-xl btn-primary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-5 h-5" />
              Add Event
            </motion.button>
          </div>

          {/* Days Header */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {days.map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Empty cells for days before first day */}
            {[...Array(firstDay)].map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            
            {/* Days */}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const isToday = day === new Date().getDate() && 
                currentDate.getMonth() === new Date().getMonth() &&
                currentDate.getFullYear() === new Date().getFullYear();
              const isSelected = day === selectedDate.getDate() &&
                currentDate.getMonth() === selectedDate.getMonth();
              const hasEvent = [15, 18, 20, 25].includes(day);

              return (
                <motion.button
                  key={day}
                  onClick={() => setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center relative transition-all ${
                    isSelected ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white' :
                    isToday ? 'bg-blue-500/20 text-blue-500' :
                    'hover:bg-gray-500/10'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="font-medium">{day}</span>
                  {hasEvent && (
                    <div className="absolute bottom-2 flex gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {day === 15 && <div className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Events Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold">Today's Schedule</h3>
            <span className="text-sm text-gray-500">{events.length} events</span>
          </div>

          <div className="space-y-4">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, x: 5 }}
                className={`p-4 rounded-xl cursor-pointer border-l-4 ${
                  event.color === 'blue' ? 'border-blue-500 bg-blue-500/10' :
                  event.color === 'green' ? 'border-green-500 bg-green-500/10' :
                  event.color === 'red' ? 'border-red-500 bg-red-500/10' :
                  'border-purple-500 bg-purple-500/10'
                }`}
              >
                <h4 className="font-medium mb-2">{event.title}</h4>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4" />
                    <span>{event.location}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Quick Add */}
          <div className="mt-6 pt-6 border-t border-gray-500/20">
            <h4 className="font-medium mb-4">Quick Add</h4>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Hearing', icon: Scale, color: 'blue' },
                { label: 'Meeting', icon: Users, color: 'green' },
                { label: 'Call', icon: Video, color: 'purple' },
                { label: 'Reminder', icon: Bell, color: 'yellow' },
              ].map((item) => (
                <motion.button
                  key={item.label}
                  className={`p-3 rounded-xl flex items-center gap-2 bg-${item.color}-500/10 hover:bg-${item.color}-500/20 transition-all`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <item.icon className={`w-4 h-4 text-${item.color}-500`} />
                  <span className="text-sm">{item.label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default CalendarPage;
